import { BiEditAlt } from "react-icons/bi";
import { FaRegQuestionCircle } from "react-icons/fa";
import { FaEllipsis } from "react-icons/fa6";
import { FiRefreshCw } from "react-icons/fi";
import { GiCheckMark } from "react-icons/gi";
import { IoHourglassOutline } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import {
  SessionTaskAttemptResultEnum,
  SessionTaskAttemptStateEnum,
} from "~/lib/objects";

interface TaskStateIconConfig {
  attemptState?: SessionTaskAttemptStateEnum;
  attemptResult?: SessionTaskAttemptResultEnum;
  hintUsed?: boolean;
  solutionEdited?: boolean;
  isCurrentAttempt?: boolean;
  retryNumber?: number;
  externalHint?: boolean;
}

const taskStateIconClassName = "mr-2 inline-block shrink-0";

export function getTaskStateIcon({
  attemptState,
  solutionEdited,
  isCurrentAttempt,
  retryNumber,
  attemptResult,
  hintUsed,
  externalHint,
}: TaskStateIconConfig): JSX.Element {
  let IconComponent: JSX.Element | undefined = undefined;
  if (
    (retryNumber ?? 0) > 0 &&
    attemptState == SessionTaskAttemptStateEnum.IN_PROGRESS
  ) {
    IconComponent = (
      <FiRefreshCw className={taskStateIconClassName} color="pink" />
    );
  } else if (solutionEdited) {
    IconComponent = <BiEditAlt className={taskStateIconClassName} />;
  } else if (
    externalHint &&
    (attemptResult === SessionTaskAttemptResultEnum.DRAW ||
      attemptResult === SessionTaskAttemptResultEnum.WIN)
  ) {
    IconComponent = (
      <span className={`${taskStateIconClassName} text-blue-500`}>½ </span>
    );
  } else {
    switch (attemptState) {
      case SessionTaskAttemptStateEnum.IN_PROGRESS:
        if (retryNumber ?? 0 > 0)
          IconComponent = (
            <FiRefreshCw className={taskStateIconClassName} color="pink" />
          );
        else
          IconComponent = (
            <IoHourglassOutline className={taskStateIconClassName} />
          );
        break;
      case SessionTaskAttemptStateEnum.HAS_MISTAKES:
        IconComponent = (
          <FaRegQuestionCircle
            className={taskStateIconClassName}
            color="#aaf"
          />
        );
        break;
      case SessionTaskAttemptStateEnum.DONE:
        if (attemptResult === SessionTaskAttemptResultEnum.LOSS)
          IconComponent = (
            <RxCross2 className={taskStateIconClassName} color="red" />
          );
        else if (
          hintUsed ||
          attemptResult === SessionTaskAttemptResultEnum.DRAW
        )
          IconComponent = (
            <span className={`${taskStateIconClassName} text-yellow-300`}>
              ½{" "}
            </span>
          );
        else
          IconComponent = (
            <GiCheckMark className={taskStateIconClassName} color="green" />
          );
        break;
      case SessionTaskAttemptStateEnum.CANCELLED:
        IconComponent = <RxCross2 className={taskStateIconClassName} />;
        break;
      default:
        IconComponent = isCurrentAttempt ? (
          <FaEllipsis className={taskStateIconClassName} />
        ) : (
          <div
            style={{
              display: "inline",
              flexShrink: 0,
              marginRight: "1.5em",
            }}
          />
        );
    }
  }

  return IconComponent;
}

export function getRatingDeltaText(
  pointsChange?: number,
  rawText = false,
): JSX.Element | undefined {
  if (pointsChange == null) return undefined;

  // Format using comma as decimal separator and exactly one digit after the comma
  const formatted = formatRating(pointsChange);
  if (formatted === "0") return undefined;

  if (pointsChange > 0) {
    if (rawText) return <span className="text-green-500">+{formatted}</span>;
    else return <span className="ml-2 text-green-500">(+{formatted})</span>;
  } else {
    // negative
    if (rawText) return <span className="text-red-500">{formatted}</span>;
    else return <span className="ml-2 text-red-500">({formatted})</span>;
  }

  return undefined;
}

export function formatRating(rating: number): string {
  let rounded = Math.sign(rating) * (Math.round(Math.abs(rating) * 10) / 10);
  const defaultDecimals = 1;
  rounded = Number(rounded.toFixed(defaultDecimals));

  // If the rounded value is an integer (e.g. 1.0 -> 1), display as integer without decimals
  if (Number.isInteger(rounded)) {
    return `${rounded}`;
  }
  // Format with 1 decimal, replace dot with comma
  return `${rounded.toFixed(defaultDecimals).replace(".", ",")}`;
}
